import { useContext, useState } from "react";
import * as Style from "./style/ProfileStyle";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../context/UserContext";
import { toast } from "react-toastify";
import { allFuncionalities } from "./utils/arrayFuncionalities";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import BounceCheckbox from "../CheckboxAccess/BounceCheckbox";
import AccessControl from "../../service/AccessControl";
import { Skeleton, Typography, Stack, Card, Button } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../../service/api";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

const accessControlService = new AccessControl();

export default function Role() {
  const { hierarchyLabel, saveUserData, setLoadingFuncionalities } = useContext(UserContext);
  const { t, i18n } = useTranslation("translation");
  const [access, setAccess] = useState<string[]>([]);
  const [role, setRole] = useState("Usuario");

  const {
    data,
    isLoading: isLoadingFuncs,
    isFetching: isFetchingFuncs,
  } = useQuery({
    queryKey: ["GET_FUNCIONALITIES_LIST"],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token not found");
      const promise = toast.promise(
        async () => {
          const response = await accessControlService.getAllFunctionalities(token, token.userEmpresa);
          if (!response) throw new Error("No data returned");
          getRoleAccess(role);
          return Array.isArray(response) ? response : [];
        },
        {
          pending: "Carregando funcionalidades...",
          success: "Funcionalidades carregadas!",
          error: "Erro ao carregar funcionalidades",
        },
      );
      return promise;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ["UPDATE_USER_FUNCIONALITIES"],
    mutationFn: async () => {
      const promise = toast.promise(
        async () => {
          const token = await AttToken();
          if (token) {
            const result = await accessControlService.changeByRole(token, role, token.userEmpresa, access);
            if (!result) throw new Error("No data returned");
            return result;
          }
        },
        {
          pending: "Alterando permissões...",
          success: "Permissões alteradas com sucesso!",
          error: "Erro ao alterar permissões",
        },
      );
      return promise;
    },
    onSuccess: () => {
      getUser();
    },
  });

  const { mutate: getUser } = useMutation({
    mutationKey: ["GET_USERDATA"],
    mutationFn: async () => {
      const promise = toast.promise(
        async () => {
          setLoadingFuncionalities(true);
          const token = await AttToken();
          if (!token) throw new Error("Token not found");
          const response = await api.post('/usuarios/getUser', token);
          if (!response.data.user) throw new Error("No user data returned");
          return response.data.user;
        },
        {
          pending: "Atualizando dados do usuário...",
          success: "Dados do usuário atualizados!",
          error: "Erro ao atualizar dados do usuário",
        },
      );
      return promise;
    },
    onSuccess: (userData) => {
      saveUserData(userData);
    },
    onSettled: () => {
      setLoadingFuncionalities(false);
    },
  });

  const { mutate: getRoleAccess, isPending: isLoading } = useMutation({
    mutationKey: ["GET_ROLE_FUNCTION"],
    mutationFn: async (selectedRole: string) => {
      const token = await AttToken();
      if (!token) throw new Error("Token not found");
      const promise = toast.promise(
        async () => {
          const response = await accessControlService.getAllByRole(token, token.userEmpresa, selectedRole);
          if (!response) throw new Error("No data returned");
          if (Array.isArray(response)) {
            setAccess(response);
            return response;
          } else {
            setAccess([]);
            return [];
          }
        },
        {
          pending: "Carregando permissões do perfil...",
          success: "Permissões do perfil carregadas!",
          error: "Erro ao carregar permissões do perfil",
        },
      );
      return promise;
    },
  });

  const roles = {
    Gerente: t("profiles.manager"),
    Supervisor: "Supervisor",
    Usuario: t("profiles.user"),
  };

  const onLoading = isLoading || isLoadingFuncs || isFetchingFuncs || isPending;

  function Loading() {
    return Array(7)
      .fill(0)
      .map((_, index) => <Skeleton key={index} variant="text" height={20} width={"200px"} />);
  }

  return (
    <Card sx={{ padding: "20px", width: "100%" }} elevation={3}>
      <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
        <FormControl fullWidth>
          <InputLabel>{t("settingsPage.profile.select")}</InputLabel>
          <Select
            value={role ?? "Usuario"}
            label={t("settingsPage.profile.select")}
            onChange={(e) => {
              setRole(e.target.value);
              getRoleAccess(e.target.value);
            }}>
            {Object.keys(roles).map((e) => (
              <MenuItem key={e} value={e}>
                {roles[e as keyof typeof roles]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography color="textDisabled" fontSize="large" fontWeight="bold">
          {t("settingsPage.profile.whichFunc")}
        </Typography>
        {onLoading ? (
          <Loading />
        ) : (
          <>
            {data
              ?.filter((e: any) => e !== "home")
              ?.filter((key) => key !== "powerbi")
              ?.map((key: any, index: any) => {
                const keyFind = allFuncionalities.find((func) => func.key === key);
                if (keyFind?.key === "estatisticas") {
                  keyFind.name = "sidebar.powerBi";
                }
                const name = keyFind ? keyFind.name : "";

                return (
                  <Style.StyledLabel key={index} checked={access.some((el: string) => el?.toLowerCase() === key?.toLowerCase())}>
                    <BounceCheckbox
                      id={`checkbox-circle-${index}`}
                      checked={access.some((el: string) => el?.toLowerCase() === key?.toLowerCase())}
                      onChange={() => {
                        trackEventMatomo("gestão de perfil", "click", "input", "edita valor");
                        if (access.some((el: string) => el?.toLowerCase() === key?.toLowerCase())) {
                          setAccess(access.filter((el) => el !== key));
                        } else setAccess([...access, key]);
                      }}
                    />
                    &nbsp;&nbsp;
                    {key === "locais"
                      ? `${i18n.resolvedLanguage !== "en" ? `${t(name)}` : `${hierarchyLabel.local} ${t(name)}`}`
                      : key === "gerencias"
                        ? `${i18n.resolvedLanguage !== "en" ? `${t(name)} ${hierarchyLabel.gerencia}` : `${hierarchyLabel.gerencia} ${t(name)}`}`
                        : t(name)}
                  </Style.StyledLabel>
                );
              })}
          </>
        )}
        <Button
          color="success"
          variant="contained"
          disabled={onLoading}
          onClick={() => {
            trackEventMatomo("gestão de perfil", "click", "button", "salva");
            mutate();
          }}>
          {`${t("settingsPage.profile.givePermissions")} ${role === "Usuario" ? "usuário" : role}.`}
        </Button>
      </Stack>
    </Card>
  );
}
